import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Switch from "@mui/material/Switch";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";
import bgImage from "assets/images/illustrations/LoginCover4.webp";
import { RequestService } from "services/RequestService";
import MDSnackbar from "components/MDSnackbar";
import Cookies from "js-cookie";

function SignIn() {
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);
  const toggleSnackbar = () => setShow(!show);
  const toggleSnackbar2 = () => setError(!error);
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const [attempts, setAttempts] = useState([]);

  // Load attempts from localStorage on component mount
  useEffect(() => {
    const savedAttempts = localStorage.getItem("loginAttempts");
    if (savedAttempts) {
      setAttempts(JSON.parse(savedAttempts));
    }
  }, []);

  // Check for blocking status on component mount
  useEffect(() => {
    const blockUntil = localStorage.getItem("loginBlockUntil");
    if (blockUntil) {
      const remainingTime = Math.max(0, parseInt(blockUntil) - Date.now());
      if (remainingTime > 0) {
        setIsBlocked(true);
        setBlockTimeRemaining(Math.floor(remainingTime / 1000));
        startCountdown(remainingTime);
      } else {
        localStorage.removeItem("loginBlockUntil");
      }
    }
  }, []);

  const startCountdown = (duration) => {
    const interval = setInterval(() => {
      setBlockTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsBlocked(false);
          localStorage.removeItem("loginBlockUntil");
          localStorage.removeItem("loginAttempts");
          setAttempts([]);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  };

  const handleSetRememberMe = () => {
    setRememberMe((prev) => !prev);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError("الرجاء إدخال بريد إلكتروني صحيح");
    } else {
      setEmailError("");
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  const updateLoginAttempts = () => {
    const now = Date.now();
    const oneMinuteAgo = now - 60000; // 1 minute in milliseconds
    
    // Filter attempts in the last minute
    const recentAttempts = attempts.filter(time => time > oneMinuteAgo);
    
    // Add current attempt
    const updatedAttempts = [...recentAttempts, now];
    setAttempts(updatedAttempts);
    localStorage.setItem("loginAttempts", JSON.stringify(updatedAttempts));
    
    return updatedAttempts.length;
  };

  const blockUser = () => {
    const blockUntil = Date.now() + 300000; // 5 minutes in milliseconds
    localStorage.setItem("loginBlockUntil", blockUntil.toString());
    setIsBlocked(true);
    setBlockTimeRemaining(300); // 5 minutes in seconds
    startCountdown(300000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (isBlocked) {
      setMessage(`لقد تجاوزت الحد المسموح من محاولات الدخول. الرجاء الانتظار ${blockTimeRemaining} ثانية`);
      setError(!error);
      return;
    }
  
    // Remove all spaces from email and password
    const cleanedEmail = email.replace(/\s+/g, '');
    const cleanedPassword = password;
  
    if (!cleanedEmail || !validateEmail(cleanedEmail)) {
      setEmailError("الرجاء إدخال بريد إلكتروني صحيح");
      return;
    }
  
    const attemptCount = updateLoginAttempts();
    
    if (attemptCount > 10) {
      blockUser();
      setMessage("لقد تجاوزت الحد المسموح من محاولات الدخول (10 محاولات في الدقيقة). الرجاء الانتظار 5 دقائق");
      setError(!error);
      return;
    }
  
    let js = { 
      email: cleanedEmail, 
      password: cleanedPassword 
    };
  
    await RequestService.post("Auth/login", js).then(async (result) => {
      if (result.status === 200) {
        setMessage(result.data.message);
        setShow(!show);
        let data = result.data.data;
        Cookies.set("Authentication", data.token, { expires: 30 });
        Cookies.set("displayName", data.displayName, { expires: 30 });
        Cookies.set("userTypeName", data.userTypeName, { expires: 30 });
  
        localStorage.removeItem("loginAttempts");
        setAttempts([]);
  
        await sleep(1000);
        navigate("/pages/account/my-profile");
        window.location.reload();
      } else {
        setError(!error);
      }
    }).catch((errorResponse) => {
      setMessage(errorResponse.response?.data?.message || "فشل تسجيل الدخول");
      setError(!error);
    });
  };

  // clear cookies
  useEffect(() => {
    const signOut = async () => {
      if (!isSigningOut) {
        setIsSigningOut(true);
        let Authentication = Cookies.get("Authentication");
        
        try {
          // Call logout API
          const response = await RequestService.get('Auth/logout');
          
          if (response.data.error === false) {
            // Clear all authentication cookies
            Cookies.remove("Authentication");
            Cookies.remove("userTypeName");
            Cookies.remove("displayName");
            localStorage.removeItem("user");
            sessionStorage.clear();
            navigate("/authentication/sign-in");
          } else {
            console.error("Logout failed:", response.data.message);
          }
        } catch (error) {
          console.error("Error during logout:", error);
          Cookies.remove("Authentication");
          Cookies.remove("user");
          Cookies.remove("token");
          localStorage.removeItem("user");
          sessionStorage.clear();
          navigate("/authentication/sign-in");
        }
      }
    };

    signOut();
  }, [isSigningOut]);

  return (
    <IllustrationLayout
      title="تسجيل الدخول"
      description={isBlocked ? `لقد تجاوزت الحد المسموح. الرجاء الانتظار ${blockTimeRemaining} ثانية` : "أدخل البريد الإلكتروني وكلمة المرور للدخول"}
      illustration={bgImage}
    >
      <MDSnackbar
        color="success"
        icon="notifications"
        title="تمت العملية بنجاح"
        content={message}
        dateTime=" منذ 0 ثواني"
        open={show}
        close={toggleSnackbar}
      />
      <MDSnackbar
        color="error"
        icon="notifications"
        title=" فشلت العملية"
        content={message}
        dateTime=" منذ 0 ثواني"
        open={error}
        close={toggleSnackbar2}
      />
      <MDBox component="form" role="form" onSubmit={handleSubmit}>
        <MDBox mb={2}>
          <MDInput
            type="email"
            label="البريد الإلكتروني"
            fullWidth
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
            disabled={isBlocked}
          />
        </MDBox>
        <MDBox mb={2}>
          <MDInput
            type="password"
            label="كلمة المرور"
            fullWidth
            value={password}
            onChange={handlePasswordChange}
            disabled={isBlocked}
          />
        </MDBox>
        <MDBox display="flex" alignItems="center" ml={-1}>
          <Switch checked={rememberMe} onChange={handleSetRememberMe} disabled={isBlocked} />
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="text"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
          >
            &nbsp;&nbsp;تذكرني
          </MDTypography>
        </MDBox>
        <MDBox mt={4} mb={1}>
          <MDButton 
            type="submit" 
            variant="gradient" 
            color="info" 
            size="large" 
            fullWidth
            disabled={isBlocked}
          >
            {isBlocked ? `ممنوع (${blockTimeRemaining}ث)` : "تسجيل الدخول"}
          </MDButton>
        </MDBox>
      </MDBox>
    </IllustrationLayout>
  );
}

export default SignIn;